import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ActionButton, PageShell } from '../../components';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useAcao } from '../../authorization/useVeredito';
import psychosocialService from '../../services/psychosocialService';
import {
  PsychosocialTemplate,
  PsychosocialTemplateDetail,
  PsychosocialForm,
  PsychosocialFormDimension,
  PsychosocialFormQuestion,
  PsychosocialDimensionDirection,
  PsychosocialScaleType,
} from '../../types/psychosocial';

const steps = ['Escolher Template', 'Revisar Perguntas', 'Salvar'];

const COPSOQ_DOMAINS = [
  'Exigências no trabalho',
  'Organização do trabalho e conteúdo',
  'Relações sociais e liderança',
  'Interface trabalho-indivíduo',
  'Valores no local de trabalho',
  'Saúde e bem-estar',
  'Comportamentos ofensivos',
];

// ── Memoized sub-components ──

interface QuestionItemEditingProps {
  question: PsychosocialFormQuestion;
  dimIdx: number;
  qIdx: number;
  initialText: string;
  onConfirmEdit: (dimIdx: number, qIdx: number, text: string) => void;
  onCancelEdit: () => void;
  onScaleChange: (dimIdx: number, qIdx: number, scaleType: PsychosocialScaleType) => void;
  onReversedChange: (dimIdx: number, qIdx: number, reversed: boolean) => void;
}

const QuestionItemEditing = React.memo<QuestionItemEditingProps>(({
  question: q, dimIdx, qIdx, initialText,
  onConfirmEdit, onCancelEdit, onScaleChange, onReversedChange,
}) => {
  const [localText, setLocalText] = useState(initialText);

  const handleConfirm = useCallback(() => {
    onConfirmEdit(dimIdx, qIdx, localText);
  }, [onConfirmEdit, dimIdx, qIdx, localText]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onConfirmEdit(dimIdx, qIdx, localText);
    if (e.key === 'Escape') onCancelEdit();
  }, [onConfirmEdit, onCancelEdit, dimIdx, qIdx, localText]);

  return (
    <ListItem sx={{ flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={localText}
          onChange={(e) => setLocalText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Texto da pergunta"
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Escala</InputLabel>
            <Select
              value={q.scaleType}
              label="Escala"
              onChange={(e) => onScaleChange(dimIdx, qIdx, e.target.value as PsychosocialScaleType)}
            >
              <MenuItem value="frequency">Frequência</MenuItem>
              <MenuItem value="intensity">Intensidade</MenuItem>
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={q.reversed}
                  onChange={(e) => onReversedChange(dimIdx, qIdx, e.target.checked)}
                />
              }
              label="Invertida"
            />
            <Tooltip title="Perguntas invertidas têm a pontuação espelhada. Usado quando a formulação da pergunta tem sentido oposto à direção da dimensão (ex: pergunta positiva em dimensão de risco).">
              <InfoIcon fontSize="small" color="action" sx={{ ml: -0.5, cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Button size="small" variant="contained" onClick={handleConfirm}>
            OK
          </Button>
          <Button size="small" onClick={onCancelEdit}>
            Cancelar
          </Button>
        </Box>
      </Box>
    </ListItem>
  );
});

QuestionItemEditing.displayName = 'QuestionItemEditing';

interface QuestionItemDisplayProps {
  question: PsychosocialFormQuestion;
  dimIdx: number;
  qIdx: number;
  readOnly?: boolean;
  onStartEdit: (dimIdx: number, qIdx: number) => void;
  onRemove: (dimIdx: number, qIdx: number) => void;
}

const QuestionItemDisplay = React.memo<QuestionItemDisplayProps>(({
  question: q, dimIdx, qIdx, readOnly, onStartEdit, onRemove,
}) => {
  const badge = useMemo(() => {
    if (!q.isFromTemplate && !q.templateQuestionId) {
      return <Chip size="small" label="Personalizada" color="secondary" sx={{ ml: 1 }} />;
    }
    if (!q.isFromTemplate && q.templateQuestionId) {
      return <Chip size="small" label="Editada" color="warning" sx={{ ml: 1 }} />;
    }
    return null;
  }, [q.isFromTemplate, q.templateQuestionId]);

  return (
    <ListItem sx={{ flexWrap: 'wrap', gap: 1 }}>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span>{q.text || <em>Sem texto</em>}</span>
            {badge}
          </Box>
        }
        secondary={`Escala: ${q.scaleType === 'frequency' ? 'Frequência' : 'Intensidade'}${q.reversed ? ' (invertida)' : ''}`}
      />
      {!readOnly && (
        <ListItemSecondaryAction>
          <IconButton size="small" onClick={() => onStartEdit(dimIdx, qIdx)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onRemove(dimIdx, qIdx)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
});

QuestionItemDisplay.displayName = 'QuestionItemDisplay';

interface DimensionAccordionProps {
  dimension: PsychosocialFormDimension;
  dimIdx: number;
  defaultExpanded: boolean;
  readOnly?: boolean;
  editingQIdx: number | null;
  onStartEdit: (dimIdx: number, qIdx: number) => void;
  onConfirmEdit: (dimIdx: number, qIdx: number, text: string) => void;
  onCancelEdit: () => void;
  onRemoveQuestion: (dimIdx: number, qIdx: number) => void;
  onScaleChange: (dimIdx: number, qIdx: number, scaleType: PsychosocialScaleType) => void;
  onReversedChange: (dimIdx: number, qIdx: number, reversed: boolean) => void;
  onAddQuestion: (dimIdx: number) => void;
  onRemoveDimension: (dimIdx: number) => void;
}

const DimensionAccordion = React.memo<DimensionAccordionProps>(({
  dimension: dim, dimIdx, defaultExpanded, readOnly,
  editingQIdx,
  onStartEdit, onConfirmEdit, onCancelEdit,
  onRemoveQuestion, onScaleChange, onReversedChange,
  onAddQuestion, onRemoveDimension,
}) => {
  const questions = dim.questions ?? [];

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight="medium">{dim.dimensionName}</Typography>
          <Chip size="small" label={dim.domain} variant="outlined" />
          <Chip
            size="small"
            label={dim.direction === 'risk' ? 'Risco' : 'Protetor'}
            color={dim.direction === 'risk' ? 'error' : 'success'}
          />
          <Chip size="small" label={`${questions.length} perguntas`} />
          {!dim.isFromTemplate && (
            <Chip size="small" label="Custom" color="secondary" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {questions.map((q, qIdx) =>
            editingQIdx === qIdx ? (
              <QuestionItemEditing
                key={`${q.templateQuestionId ?? 'c'}-${q.order}-${qIdx}`}
                question={q}
                dimIdx={dimIdx}
                qIdx={qIdx}
                initialText={q.text}
                onConfirmEdit={onConfirmEdit}
                onCancelEdit={onCancelEdit}
                onScaleChange={onScaleChange}
                onReversedChange={onReversedChange}
              />
            ) : (
              <QuestionItemDisplay
                key={`${q.templateQuestionId ?? 'c'}-${q.order}-${qIdx}`}
                question={q}
                dimIdx={dimIdx}
                qIdx={qIdx}
                readOnly={readOnly}
                onStartEdit={onStartEdit}
                onRemove={onRemoveQuestion}
              />
            ),
          )}
        </List>
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAddQuestion(dimIdx)}
            >
              Adicionar Pergunta
            </Button>
            {!dim.isFromTemplate && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onRemoveDimension(dimIdx)}
              >
                Remover Dimensão
              </Button>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

DimensionAccordion.displayName = 'DimensionAccordion';

// ── Main page component ──

const PsychosocialFormBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { effectiveCompanyId } = useCompanyContext();
  const personalizar = useAcao('psychosocial_form.customize', 'Adicionar Dimensão');
  const podePersonalizar = !personalizar.disabled;
  const isEditingForm = !!id;

  const [activeStep, setActiveStep] = useState(isEditingForm ? 1 : 0);
  const [templates, setTemplates] = useState<PsychosocialTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PsychosocialTemplateDetail | null>(null);
  const [form, setForm] = useState<PsychosocialForm | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [companyIdField, setCompanyIdField] = useState('');
  const [dimensions, setDimensions] = useState<PsychosocialFormDimension[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only tracks WHICH question is being edited — text lives locally in QuestionItemEditing
  const [editingQuestion, setEditingQuestion] = useState<{ dimIdx: number; qIdx: number } | null>(null);

  // Add dimension dialog
  const [addDimDialogOpen, setAddDimDialogOpen] = useState(false);
  const [newDimName, setNewDimName] = useState('');
  const [newDimDomain, setNewDimDomain] = useState(COPSOQ_DOMAINS[0]!);
  const [newDimDirection, setNewDimDirection] = useState<PsychosocialDimensionDirection>('risk');

  // Track original dimensions for customization detection
  const originalDimensionsRef = useRef<string>('');

  useEffect(() => {
    if (isEditingForm) {
      loadForm();
    } else {
      loadTemplates();
    }
  }, [id]);

  const loadTemplates = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await psychosocialService.getTemplates();
      setTemplates(data);
    } catch {
      setError('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const loadForm = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await psychosocialService.getForm(Number(id));
      setForm(data);
      setFormName(data.name);
      setFormDescription(data.description || '');
      setDimensions(data.dimensions || []);
    } catch {
      setError('Erro ao carregar formulário');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = useCallback(async (templateId: number): Promise<void> => {
    setLoading(true);
    try {
      const template = await psychosocialService.getTemplate(templateId);
      setSelectedTemplate(template);
      const dims: PsychosocialFormDimension[] = template.dimensions.map((d) => ({
        id: 0,
        formId: 0,
        dimensionKey: d.dimensionKey,
        dimensionName: d.dimensionName,
        domain: d.domain,
        direction: d.direction,
        isFromTemplate: true,
        order: d.order,
        questions: (d.questions || []).map((q) => ({
          id: 0,
          dimensionId: 0,
          templateQuestionId: q.id,
          text: q.text,
          scaleType: q.scaleType,
          reversed: q.reversed,
          isFromTemplate: true,
          order: q.order,
        })),
      }));
      setDimensions(dims);
      originalDimensionsRef.current = JSON.stringify(dims);
      setActiveStep(1);
    } catch {
      enqueueSnackbar('Erro ao carregar template', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleRemoveQuestion = useCallback((dimIdx: number, qIdx: number): void => {
    setDimensions((prev) => {
      const newDims = [...prev];
      const dim = { ...newDims[dimIdx]! };
      const questions = [...(dim.questions || [])];
      questions.splice(qIdx, 1);
      dim.questions = questions;
      newDims[dimIdx] = dim;
      return newDims;
    });
    setEditingQuestion((prev) => {
      if (prev?.dimIdx === dimIdx && prev?.qIdx === qIdx) return null;
      return prev;
    });
    enqueueSnackbar('Pergunta removida. A remoção pode afetar o cálculo da dimensão COPSOQ.', { variant: 'warning' });
  }, [enqueueSnackbar]);

  const startEditQuestion = useCallback((dimIdx: number, qIdx: number): void => {
    setEditingQuestion({ dimIdx, qIdx });
  }, []);

  const confirmEditQuestion = useCallback((dimIdx: number, qIdx: number, text: string): void => {
    const trimmed = text.trim();
    if (!trimmed) {
      enqueueSnackbar('O texto da pergunta não pode ser vazio', { variant: 'warning' });
      return;
    }
    setDimensions((prev) => {
      const newDims = [...prev];
      const dim = { ...newDims[dimIdx]! };
      const questions = [...(dim.questions || [])];
      questions[qIdx] = { ...questions[qIdx]!, text: trimmed, isFromTemplate: false };
      dim.questions = questions;
      newDims[dimIdx] = dim;
      return newDims;
    });
    setEditingQuestion(null);
  }, [enqueueSnackbar]);

  const cancelEditQuestion = useCallback((): void => {
    setEditingQuestion(null);
  }, []);

  const handleAddQuestion = useCallback((dimIdx: number): void => {
    setDimensions((prev) => {
      const newDims = [...prev];
      const dim = { ...newDims[dimIdx]! };
      const questions = [...(dim.questions || [])];
      const newQuestion: PsychosocialFormQuestion = {
        id: 0,
        dimensionId: 0,
        templateQuestionId: null,
        text: '',
        scaleType: 'frequency',
        reversed: false,
        isFromTemplate: false,
        order: questions.length + 1,
      };
      questions.push(newQuestion);
      dim.questions = questions;
      newDims[dimIdx] = dim;
      setEditingQuestion({ dimIdx, qIdx: questions.length - 1 });
      return newDims;
    });
  }, []);

  const handleQuestionScaleChange = useCallback((dimIdx: number, qIdx: number, scaleType: PsychosocialScaleType): void => {
    setDimensions((prev) => {
      const newDims = [...prev];
      const dim = { ...newDims[dimIdx]! };
      const questions = [...(dim.questions || [])];
      questions[qIdx] = { ...questions[qIdx]!, scaleType };
      dim.questions = questions;
      newDims[dimIdx] = dim;
      return newDims;
    });
  }, []);

  const handleQuestionReversedChange = useCallback((dimIdx: number, qIdx: number, reversed: boolean): void => {
    setDimensions((prev) => {
      const newDims = [...prev];
      const dim = { ...newDims[dimIdx]! };
      const questions = [...(dim.questions || [])];
      questions[qIdx] = { ...questions[qIdx]!, reversed };
      dim.questions = questions;
      newDims[dimIdx] = dim;
      return newDims;
    });
  }, []);

  const handleAddDimension = useCallback((): void => {
    const trimmedName = newDimName.trim();
    if (!trimmedName) {
      enqueueSnackbar('Informe o nome da dimensão', { variant: 'warning' });
      return;
    }
    const key = trimmedName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setDimensions((prev) => [...prev, {
      id: 0,
      formId: 0,
      dimensionKey: key,
      dimensionName: trimmedName,
      domain: newDimDomain,
      direction: newDimDirection,
      isFromTemplate: false,
      order: prev.length + 1,
      questions: [],
    }]);
    setAddDimDialogOpen(false);
    setNewDimName('');
    setNewDimDomain(COPSOQ_DOMAINS[0]!);
    setNewDimDirection('risk');
  }, [newDimName, newDimDomain, newDimDirection, enqueueSnackbar]);

  const handleRemoveDimension = useCallback((dimIdx: number): void => {
    setDimensions((prev) => {
      const newDims = [...prev];
      newDims.splice(dimIdx, 1);
      return newDims;
    });
    enqueueSnackbar('Dimensão removida', { variant: 'info' });
  }, [enqueueSnackbar]);

  const buildDimensionsPayload = useCallback((dims: PsychosocialFormDimension[]) => {
    return dims.map((d, dIdx) => ({
      dimensionKey: d.dimensionKey,
      dimensionName: d.dimensionName,
      domain: d.domain,
      direction: d.direction,
      isFromTemplate: d.isFromTemplate,
      order: dIdx + 1,
      questions: (d.questions || []).map((q, qIdx) => ({
        templateQuestionId: q.templateQuestionId,
        text: q.text,
        scaleType: q.scaleType,
        reversed: q.reversed,
        isFromTemplate: q.isFromTemplate,
        order: qIdx + 1,
      })),
    }));
  }, []);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!formName.trim()) {
      enqueueSnackbar('Informe o nome do formulário', { variant: 'warning' });
      return;
    }

    const resolvedCompanyId = Number(companyIdField) || effectiveCompanyId;
    if (!isEditingForm && !resolvedCompanyId) {
      enqueueSnackbar('Selecione a empresa', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (isEditingForm && form) {
        await psychosocialService.updateForm(form.id, {
          name: formName,
          description: formDescription || undefined,
          dimensions: buildDimensionsPayload(dimensions),
        });
        enqueueSnackbar('Formulário atualizado com sucesso', { variant: 'success' });
      } else {
        const payload: Parameters<typeof psychosocialService.createForm>[0] = {
          companyId: resolvedCompanyId!,
          baseTemplateId: selectedTemplate!.id,
          name: formName,
          description: formDescription || undefined,
        };
        if (JSON.stringify(dimensions) !== originalDimensionsRef.current) {
          payload.dimensions = buildDimensionsPayload(dimensions);
        }
        await psychosocialService.createForm(payload);
        enqueueSnackbar('Formulário criado com sucesso', { variant: 'success' });
      }
      navigate('/psychosocial');
    } catch (err: unknown) {
      const responseError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      enqueueSnackbar(
        typeof responseError === 'string' && responseError ? responseError : 'Erro ao salvar formulário',
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  }, [formName, companyIdField, effectiveCompanyId, isEditingForm, form, formDescription, dimensions, selectedTemplate, buildDimensionsPayload, navigate, enqueueSnackbar]);

  const totalQuestions = useMemo(
    () => dimensions.reduce((s, d) => s + (d.questions?.length ?? 0), 0),
    [dimensions],
  );

  // Stable per-dimension editingQIdx: only the dimension being edited gets a non-null value
  const getEditingQIdx = useCallback((dimIdx: number): number | null => {
    return editingQuestion?.dimIdx === dimIdx ? editingQuestion.qIdx : null;
  }, [editingQuestion]);

  const openAddDimDialog = useCallback(() => setAddDimDialogOpen(true), []);
  const closeAddDimDialog = useCallback(() => setAddDimDialogOpen(false), []);
  const goToStep0 = useCallback(() => setActiveStep(0), []);
  const goToStep1 = useCallback(() => setActiveStep(1), []);
  const goToStep2 = useCallback(() => setActiveStep(2), []);

  const breadcrumbs = [
    { label: 'Psicossocial', path: '/psychosocial' },
    { label: 'Formulários', path: '/psychosocial' },
    { label: isEditingForm ? 'Editar' : 'Novo' },
  ];
  const pageTitle = isEditingForm ? 'Editar Formulário' : 'Novo Formulário';

  if (loading) {
    return (
      <PageShell
        breadcrumbs={breadcrumbs}
        title={pageTitle}
        subtitle="Crie formulários a partir de templates COPSOQ"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={pageTitle}
      subtitle="Crie formulários a partir de templates COPSOQ"
    >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {/* Step 0: Choose template */}
        {activeStep === 0 && (
          <Grid container spacing={3}>
            {templates.map((t) => (
              <Grid size={{ xs: 12, md: 4 }} key={t.id}>
                <Card>
                  <CardActionArea onClick={() => handleSelectTemplate(t.id)}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{t.name}</Typography>
                      <Chip
                        size="small"
                        label={`${t.totalQuestions} perguntas`}
                        color="primary"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Step 1: Review questions */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {podePersonalizar
                ? 'Revise as dimensões e perguntas. Você pode editar, remover ou adicionar perguntas e dimensões.'
                : 'Revise as dimensões e perguntas do template.'}
            </Typography>
            {dimensions.map((dim, dIdx) => (
              <DimensionAccordion
                key={`${dim.dimensionKey}-${dIdx}`}
                dimension={dim}
                dimIdx={dIdx}
                defaultExpanded={dIdx === 0}
                readOnly={!podePersonalizar}
                editingQIdx={getEditingQIdx(dIdx)}
                onStartEdit={startEditQuestion}
                onConfirmEdit={confirmEditQuestion}
                onCancelEdit={cancelEditQuestion}
                onRemoveQuestion={handleRemoveQuestion}
                onScaleChange={handleQuestionScaleChange}
                onReversedChange={handleQuestionReversedChange}
                onAddQuestion={handleAddQuestion}
                onRemoveDimension={handleRemoveDimension}
              />
            ))}

            <Box sx={{ mt: 2 }}>
              <ActionButton
                acao="psychosocial_form.customize"
                rotulo="Adicionar Dimensão"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openAddDimDialog}
              >
                Adicionar Dimensão
              </ActionButton>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
              {!isEditingForm && (
                <Button onClick={goToStep0}>Voltar</Button>
              )}
              <Button variant="contained" onClick={goToStep2}>Próximo</Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Name and save */}
        {activeStep === 2 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!isEditingForm && (
                  <CompanySelectField
                    value={companyIdField}
                    onChange={setCompanyIdField}
                    required
                  />
                )}
                <TextField
                  label="Nome do Formulário"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="Descrição (opcional)"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
                <Typography variant="body2" color="text.secondary">
                  {dimensions.length} dimensões, {totalQuestions} perguntas
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                <Button onClick={goToStep1}>Voltar</Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar Formulário'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add Dimension Dialog */}
        <Dialog open={addDimDialogOpen} onClose={closeAddDimDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Adicionar Dimensão</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Nome da Dimensão"
                value={newDimName}
                onChange={(e) => setNewDimName(e.target.value)}
                required
                fullWidth
                autoFocus
              />
              <FormControl fullWidth>
                <InputLabel>Domínio</InputLabel>
                <Select
                  value={newDimDomain}
                  label="Domínio"
                  onChange={(e) => setNewDimDomain(e.target.value)}
                >
                  {COPSOQ_DOMAINS.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Direção</InputLabel>
                <Select
                  value={newDimDirection}
                  label="Direção"
                  onChange={(e) => setNewDimDirection(e.target.value as PsychosocialDimensionDirection)}
                >
                  <MenuItem value="risk">Risco</MenuItem>
                  <MenuItem value="protective">Protetor</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAddDimDialog}>Cancelar</Button>
            <Button variant="contained" onClick={handleAddDimension}>Adicionar</Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default PsychosocialFormBuilderPage;
